/**
 * ConfirmDeleteModal Component
 * Reusable delete confirmation modal with gradient styling
 */

import { forwardRef } from 'react';
import { Modal, Stack, Text, Group, Button } from '@/components/ui';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { GradientVariant } from '../../types';

export interface ConfirmDeleteModalProps {
  /** Whether the modal is opened */
  opened: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when deletion is confirmed */
  onConfirm: () => void;
  /** Modal title */
  title?: string;
  /** Confirmation message */
  message?: string;
  /** Name of the item being deleted */
  itemName?: string;
  /** Number of items being deleted (for bulk operations) */
  itemCount?: number;
  /** Gradient variant for danger styling */
  gradientVariant?: GradientVariant;
  /** Confirm button label */
  confirmLabel?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Loading state during deletion */
  loading?: boolean;
}

/**
 * Confirmation modal for delete operations
 *
 * @example
 * ```tsx
 * <ConfirmDeleteModal
 *   opened={deleteModalOpened}
 *   onClose={() => setDeleteModalOpened(false)}
 *   onConfirm={() => handleDelete()}
 *   title="Delete File"
 *   message="Are you sure you want to delete this file?"
 *   itemName="document.pdf"
 * />
 * ```
 */
export const ConfirmDeleteModal = forwardRef<HTMLDivElement, ConfirmDeleteModalProps>(
  (props, ref) => {
    const {
      opened,
      onClose,
      onConfirm,
      title = 'Confirm Deletion',
      message,
      itemName,
      itemCount,
      gradientVariant = 'danger',
      confirmLabel = 'Delete',
      cancelLabel = 'Cancel',
      loading = false,
    } = props;

    const getDefaultMessage = (): string => {
      if (itemCount && itemCount > 1) {
        return `Are you sure you want to delete ${itemCount} items? This action cannot be undone.`;
      }
      if (itemName) {
        return `Are you sure you want to delete "${itemName}"? This action cannot be undone.`;
      }
      return 'Are you sure you want to delete this item? This action cannot be undone.';
    };

    const displayMessage = message || getDefaultMessage();

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={title}
        size="md"
        centered
        variant="gradient"
        gradientVariant={gradientVariant}
      >
        <Stack gap="lg">
          {/* Warning Icon */}
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-red-0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
            }}
          >
            <IconAlertTriangle size={32} color="var(--mantine-color-red-6)" />
          </div>

          {/* Message */}
          <Stack gap="xs" style={{ textAlign: 'center' }}>
            <Text size="md">{displayMessage}</Text>
            {itemCount && itemCount > 1 && (
              <Text size="sm" c="dimmed">
                {itemCount} {itemCount === 1 ? 'item' : 'items'} will be deleted
              </Text>
            )}
          </Stack>

          {/* Actions */}
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={onClose} disabled={loading}>
              {cancelLabel}
            </Button>
            <Button
              variant="filled"
              gradientVariant={gradientVariant}
              onClick={onConfirm}
              loading={loading}
              leftSection={!loading ? <IconAlertTriangle size={16} /> : undefined}
            >
              {confirmLabel}
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }
);

ConfirmDeleteModal.displayName = 'ConfirmDeleteModal';

export default ConfirmDeleteModal;
